import { Box, Text } from "@chakra-ui/react";
import { isPopulatedArray, toNumberDisplayValue } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { ErrorBoundary } from "components";
import { useCallback, useMemo } from "react";
import { byLook } from "theme/look";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import { BrandChart, ChartExportSvg, useBrandChartTheme } from "../BrandChart";
import { buildVideoCompletionRateDefinition } from "./VideoCompletionRateChart.definition";
import {
  LegacyVideoCompletionRateChart,
  VideoCompletionRateChartProps,
} from "./legacy/VideoCompletionRateChart";

export type { VideoCompletionRateChartProps };

const BAR_HEIGHT = 44;
const AXIS_SPACE = 40;
const MAX_HEIGHT = 300;

const BrandVideoCompletionRateChart = ({
  isLoading,
  data,
  stretch = false,
  noDataMessage,
  id,
  exportMode,
  title,
  minHeight,
}: VideoCompletionRateChartProps) => {
  const brandTheme = useBrandChartTheme();

  const definition = useMemo(
    () =>
      defineChart({
        chart: ({ width }) =>
          buildVideoCompletionRateDefinition(data, brandTheme, width),
        tooltip: { use: tooltip },
      }),
    [data, brandTheme],
  );

  const buildExportDefinition = useCallback(
    ({ width }: { width: number }) =>
      buildVideoCompletionRateDefinition(data, brandTheme, width),
    [data, brandTheme],
  );

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data)) {
    return (
      <NoData message={noDataMessage} height={minHeight} stretch={stretch} />
    );
  }

  const ariaLabel = title ?? "Average % watched per creative";

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildExportDefinition}
      />
    );
  }

  const contentHeight = BAR_HEIGHT * data.length + AXIS_SPACE;

  return (
    <ErrorBoundary>
      {/* Long creative lists scroll inside the card, as before. */}
      <Box
        width="100%"
        maxH={stretch ? undefined : `${MAX_HEIGHT}px`}
        overflowY="auto"
        minHeight={minHeight}
      >
        <BrandChart
          definition={definition}
          brandTheme={brandTheme}
          ariaLabel={ariaLabel}
          height={`${contentHeight}px`}
          renderTooltipBody={({ points }) => {
            const item = points[0]?.datum;

            if (!item) {
              return null;
            }

            return (
              <Text fontSize="sm">
                <strong>{item.label}:</strong>{" "}
                {toNumberDisplayValue(item.value)}% watched
              </Text>
            );
          }}
        />
      </Box>
    </ErrorBoundary>
  );
};

const VideoCompletionRateChart = byLook(
  BrandVideoCompletionRateChart,
  LegacyVideoCompletionRateChart,
);

export { VideoCompletionRateChart };
